import React, { useState, useEffect } from 'react';
import { discountCategoriesAPI } from '../../services/api';

interface DiscountCategory {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  is_custom: boolean;
}

const DiscountCategories: React.FC = () => {
  const [categories, setCategories] = useState<DiscountCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const loadCategories = async () => {
    try {
      const res = await discountCategoriesAPI.list();
      setCategories(res.data);
    } catch {
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCategories(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setError('');
    try {
      await discountCategoriesAPI.create({ name: newName.trim(), description: newDescription.trim() });
      setNewName('');
      setNewDescription('');
      loadCategories();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create category');
    }
  };

  const handleSaveEdit = async (id: number) => {
    setError('');
    try {
      await discountCategoriesAPI.update(id, { name: editName.trim(), description: editDescription.trim() });
      setEditingId(null);
      loadCategories();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update category');
    }
  };

  const handleToggleActive = async (cat: DiscountCategory) => {
    try {
      await discountCategoriesAPI.update(cat.id, { is_active: !cat.is_active });
      loadCategories();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update category');
    }
  };

  const startEdit = (cat: DiscountCategory) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditDescription(cat.description);
  };

  const displayed = showInactive ? categories : categories.filter(c => c.is_active);

  if (loading) return <div style={styles.container}><p>Loading...</p></div>;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Discount Categories</h1>
      <p style={styles.subtitle}>Manage categories for tracking invoice discounts</p>

      {error && <div style={styles.error}>{error}</div>}

      {/* Add form */}
      <div style={styles.addForm}>
        <h3 style={{ margin: '0 0 8px' }}>Add Category</h3>
        <div style={styles.addRow}>
          <input
            style={styles.input}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Category name"
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <input
            style={{ ...styles.input, flex: 2 }}
            value={newDescription}
            onChange={e => setNewDescription(e.target.value)}
            placeholder="Description (optional)"
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <button style={styles.addBtn} onClick={handleAdd}>Add</button>
        </div>
      </div>

      {/* Toggle inactive */}
      <label style={styles.toggle}>
        <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
        Show inactive categories
      </label>

      {/* Table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Description</th>
            <th style={styles.th}>Status</th>
            <th style={{ ...styles.th, width: 150 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {displayed.map(cat => (
            <tr key={cat.id} style={{ opacity: cat.is_active ? 1 : 0.5 }}>
              <td style={styles.td}>
                {editingId === cat.id ? (
                  <input style={styles.editInput} value={editName} onChange={e => setEditName(e.target.value)} />
                ) : (
                  <span>{cat.name}{cat.is_custom && <span style={styles.customBadge}>Custom</span>}</span>
                )}
              </td>
              <td style={styles.td}>
                {editingId === cat.id ? (
                  <input style={{ ...styles.editInput, width: '100%' }} value={editDescription} onChange={e => setEditDescription(e.target.value)} />
                ) : (
                  cat.description
                )}
              </td>
              <td style={styles.td}>
                <span style={{ ...styles.statusBadge, background: cat.is_active ? '#dcfce7' : '#fee2e2', color: cat.is_active ? '#166534' : '#991b1b' }}>
                  {cat.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td style={styles.td}>
                {editingId === cat.id ? (
                  <>
                    <button style={styles.saveBtn} onClick={() => handleSaveEdit(cat.id)}>Save</button>
                    <button style={styles.cancelBtn} onClick={() => setEditingId(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button style={styles.editBtn} onClick={() => startEdit(cat)}>Edit</button>
                    <button style={styles.deactivateBtn} onClick={() => handleToggleActive(cat)}>
                      {cat.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {displayed.length === 0 && (
            <tr><td colSpan={4} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>No categories found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: 900, margin: '0 auto', padding: 24 },
  title: { fontSize: 24, fontWeight: 700, margin: '0 0 4px' },
  subtitle: { color: '#666', margin: '0 0 20px' },
  error: { background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 16 },
  addForm: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 },
  addRow: { display: 'flex', gap: 8 },
  input: { flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 },
  addBtn: { padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 },
  toggle: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 14, color: '#666', cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: { textAlign: 'left' as const, padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: 13, fontWeight: 600, color: '#374151' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 14 },
  editInput: { padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 },
  statusBadge: { padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 },
  customBadge: { marginLeft: 6, padding: '1px 6px', background: '#e0e7ff', color: '#3730a3', borderRadius: 8, fontSize: 11 },
  editBtn: { padding: '4px 10px', background: '#f1f5f9', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', marginRight: 4, fontSize: 13 },
  deactivateBtn: { padding: '4px 10px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
  saveBtn: { padding: '4px 10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', marginRight: 4, fontSize: 13 },
  cancelBtn: { padding: '4px 10px', background: '#f1f5f9', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
};

export default DiscountCategories;
